import * as React from "react"

import { useTheme } from "@/contexts/ThemeContext"
import { cn } from "@/lib/utils"
import { useOrder } from "../../contexts/OrderContext"
import { supabase } from "@/lib/supabase"
import { DollarSignIcon, FileIcon, FileTextIcon, LoaderCircleIcon, ReceiptIcon, ScanTextIcon } from "lucide-react"
import { hr } from "date-fns/locale"
import { Card } from "@/components/ui/card"
import { examples } from "@/cournoyer"

export default function PDF({ path, invoice, displayLoading }) {
  const [loading, setLoading] = React.useState(true)
  const [uri, setUri] = React.useState(null)
  const { order } = useOrder()
  
  const viewer = React.useRef(null)
  const instance = React.useRef(null)

  const { theme } = useTheme()

  React.useEffect(() => {
    if (instance.current) {
      const theme_ = instance.current.UI.Theme
      instance.current.UI.setTheme(theme === 'dark' ? theme_.DARK : theme_.LIGHT)
    }
  }, [theme])

  const fetchUri = async () => {
    setLoading(true);
    
    if (!invoice) {
      // Vérifie si le fichier est en cache
      const cachedBlob = await getCachedFile(path);
      if (cachedBlob) {
        console.log("📂 Chargement du PDF depuis le cache");
        setUri(URL.createObjectURL(cachedBlob.blob));
        return;
      }
    }
  
    // Sinon, télécharge et stocke
    let url
    if (examples.some(example => path.includes(example))) {
      url = `/cournoyer/${path}`
    } else {
      const { data } = await supabase.storage.from(invoice ? "invoices" : "folders").createSignedUrl(`${path}`, 60);
      if (!data?.signedUrl) {
        console.error("❌ Impossible de récupérer le lien signé !");
        return;
      }

      url = data?.signedUrl
    }
    
  
    const response = await fetch(url);
    const blob = await response.blob();
  
    // Stocker en cache
    await cacheFile(path, blob, order.pydio_id);
  
    setUri(URL.createObjectURL(blob));
  };
  
  

  React.useEffect(() => {
    fetchUri()
  }, [path])


  React.useEffect(() => {
    if (!uri) return;

    if (instance?.current) {
      instance.current.loadDocument(uri)
    } else {
      const WebViewer = window.WebViewer

      let _instance;

      WebViewer(
        {
          licenseKey: 'ZdbGu7G2LXX3U6zMSJZ5',
          path: '/',
          initialDoc: uri,
          disabledElements: ['menuButton']
        },
        viewer.current,
      ).then((__instance) => {
        _instance = __instance
        instance.current = _instance

        const { Core } = _instance

        const theme_ = _instance.UI.Theme
        _instance.UI.setTheme(theme === 'dark' ? theme_.DARK : theme_.LIGHT)

        Core.documentViewer.addEventListener('documentLoaded', () => {
          setLoading(false)

          var FitMode = _instance.UI.FitMode
          _instance.UI.setFitMode(FitMode.FitWidth)
        })
      })
    }
  }, [uri])

  React.useEffect(() => {
    return () => {
      if (instance?.current) {
        instance.current.UI.dispose()
        instance.current = null
      }
    }
  }, [])

  return <Card className="flex flex-grow overflow-hidden relative h-full w-full">
    {(loading || displayLoading) && <div className="top-0 left-0 z-[50] flex bg-background justify-center items-center absolute h-full w-full">
      <div className="flex flex-col items-center gap-4 text-muted-foreground transform translate-y-[-50%]">
        {!invoice && <FileTextIcon className="h-16 w-16" strokeWidth={1} />}
        {!invoice && <p className="text-sm flex items-center justify-center text-lg select-none">
          <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
          Chargement du document
        </p>}

        {invoice && <ReceiptIcon className="h-16 w-16" strokeWidth={1} />}
        {invoice && <p className="text-sm flex items-center justify-center text-lg select-none">
          <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
          Chargement de la facture
        </p>}
      </div>
    </div>}

    <div className={cn("webviewer")} ref={viewer} style={{ position: 'relative', height: '100%', width: '100%' }}></div>
  </Card>
}

export const openDatabase = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("fileCacheDB", 1);

    // Set up the database when first created or upgraded
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("files")) {
        const objectStore = db.createObjectStore("files", { keyPath: "key" });  // Use `key` as the primary key
        objectStore.createIndex("pydioId", "pydioId", { unique: false });
      }
    };

    // Resolve with the database instance once open
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    // Reject if there's an error opening the database
    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

export const cacheFile = async (key, blob, pydioId) => {
  const db = await openDatabase();
  const tx = db.transaction("files", "readwrite"); // Transaction mode "readwrite" to modify data
  const store = tx.objectStore("files");

  let fileData = {
    key,       // Manually provided key (unique file identifier)
    blob,      // The Blob object (PDF file)
    pydioId,   // The pydioId associated with the file
  };

  if (examples.some(example => key.includes(example))) {
    delete fileData['pydioId']
  }

  return new Promise((resolve, reject) => {
    // Store or update the file in the object store
    const request = store.put(fileData);  // `put` will add or replace the record

    request.onsuccess = () => {
      console.log(`📝 File [${key}] successfully added/updated`);
      resolve();  // Resolve when the operation is successful
    };

    request.onerror = (e) => {
      console.error(`❌ Error adding file [${key}]:`, e.target.error);
      reject(e.target.error);  // Reject on error
    };
  });
};

export const getCachedFile = async (key) => {
  const db = await openDatabase();
  const tx = db.transaction("files", "readonly"); // Use readonly mode for fetching data
  const store = tx.objectStore("files");

  return new Promise((resolve, reject) => {
    // Fetch the file by its key
    const request = store.get(key);

    request.onsuccess = () => {
      const file = request.result; // Get the result of the request
      if (file) {
        console.log("Cached File:", file);  // Logs the cached file with its `blob` and `pydioId`
        resolve(file);  // Resolve with the file data
      } else {
        console.log(`No cached file found for key: ${key}`);
        resolve(null);  // Resolve with null if no file was found
      }
    };

    request.onerror = () => {
      reject(request.error);  // Reject if there was an error retrieving the file
    };
  });
};

export const removeFilesByPydioId = async (targetPydioId) => {
  const db = await openDatabase();
  const tx = db.transaction("files", "readwrite"); // Transaction mode "readwrite" to modify data
  const store = tx.objectStore("files");
  const index = store.index("pydioId"); // Use the "pydioId" index for filtering files

  return new Promise((resolve, reject) => {
    const request = index.openCursor();  // Open a cursor to iterate over files

    request.onsuccess = (e) => {
      const cursor = e.target.result;

      if (cursor) {
        const file = cursor.value;
        
        // If the file's pydioId doesn't match the target value, delete it
        if (file.pydioId !== targetPydioId) {
          store.delete(file.key);  // Delete the file using its key
          console.log(`🗑️ File [${file.key}] with pydioId [${file.pydioId}] deleted.`);
        }
        
        cursor.continue();  // Move to the next record
      } else {
        console.log("Completed file deletion process.");
        resolve();  // Resolve when all files have been processed
      }
    };

    request.onerror = (e) => {
      console.error("❌ Error while iterating files:", e.target.error);
      reject(e.target.error);  // Reject if there's an error iterating over files
    };
  });
};
